import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  CalendarToday as CalendarIcon,
  Warning as WarningIcon,
  Google as GoogleIcon,
  FileUpload as FileIcon
} from '@mui/icons-material';
import { parseICS, readICSFile, validateEventsForTrip, convertEventsToItinerary, isICSFile } from '../utils/icsParser';
import { parseGoogleCalendarEvents, loadGoogleCalendarEvents, isGoogleCalendarAvailable, authenticateGoogleCalendar, getGoogleCalendarList } from '../utils/icsParser';
import api from '../config/api';

const ICSImportDialog = ({ open, onClose, trip, onSuccess }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [importMethod, setImportMethod] = useState('file'); // 'file' or 'google'
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedCalendar, setSelectedCalendar] = useState('primary');
  const [availableCalendars, setAvailableCalendars] = useState([]);
  const [parsedEvents, setParsedEvents] = useState([]);
  const [validationResult, setValidationResult] = useState(null);
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [autoDetect, setAutoDetect] = useState(true);

  const steps = [
    'Choose Import Method',
    'Select Source',
    'Review Events',
    'Import to Trip'
  ];

  const handleClose = useCallback(() => {
    setActiveStep(0);
    setSelectedFile(null);
    setSelectedCalendar('primary');
    setParsedEvents([]);
    setValidationResult(null);
    setSelectedEvents([]);
    setError(null);
    setAutoDetect(true);
    onClose();
  }, [onClose]);

  const handleNext = useCallback(() => {
    setActiveStep(prev => prev + 1);
  }, []);

  const handleBack = useCallback(() => {
    setActiveStep(prev => prev - 1);
  }, []);

  const handleFileSelect = useCallback(async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!isICSFile(file)) {
      setError('Please select a valid ICS file (.ics or .ical)');
      return;
    }

    setSelectedFile(file);
    setError(null);
  }, []);

  const handleGoogleCalendarAuth = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Check if Google Calendar API is available
      if (!isGoogleCalendarAvailable()) {
        throw new Error('Google Calendar API not available. Please ensure Google APIs are loaded.');
      }

      // Authenticate with Google Calendar
      await authenticateGoogleCalendar();
      
      // Get available calendars
      const calendars = await getGoogleCalendarList();
      setAvailableCalendars(calendars);
      
      handleNext();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [handleNext]);

  const handleParseEvents = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      let events = [];

      if (importMethod === 'file' && selectedFile) {
        // Parse ICS file
        const icsContent = await readICSFile(selectedFile);
        events = parseICS(icsContent);
      } else if (importMethod === 'google' && selectedCalendar) {
        // Load Google Calendar events
        const startDate = new Date(trip.start_date);
        const endDate = new Date(trip.end_date);
        endDate.setHours(23, 59, 59, 999); // Include entire end date
        
        const googleEvents = await loadGoogleCalendarEvents(selectedCalendar, startDate, endDate);
        events = parseGoogleCalendarEvents(googleEvents);
      }

      // Validate events against trip date range
      const validation = validateEventsForTrip(events, trip.start_date, trip.end_date);
      
      setParsedEvents(events);
      setValidationResult(validation);
      setSelectedEvents(autoDetect ? validation.validEvents : []);
      
      handleNext();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [importMethod, selectedFile, selectedCalendar, trip, autoDetect, handleNext]);

  const handleEventToggle = useCallback((event, checked) => {
    if (checked) {
      setSelectedEvents(prev => [...prev, event]);
    } else {
      setSelectedEvents(prev => prev.filter(e => e !== event));
    }
  }, []);

  const handleImportEvents = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const itineraryItems = convertEventsToItinerary(selectedEvents, trip.id, trip.user_id);
      console.log('📅 ICS Import: Importing', itineraryItems.length, 'events');
      
      // Import each event
      for (const item of itineraryItems) {
        console.log('📅 ICS Import: Creating event:', item.name);
        const response = await api.post(`/trips/${trip.id}/itinerary`, item);
        console.log('📅 ICS Import: Event created successfully:', response.data);
      }

      console.log('📅 ICS Import: All events imported successfully');
      
      // Small delay to ensure backend processing is complete
      setTimeout(() => {
        onSuccess?.();
        handleClose();
      }, 500);
    } catch (err) {
      console.error('📅 ICS Import Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedEvents, trip, onSuccess, handleClose]);

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Choose how you'd like to import events to your trip:
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <Button
                variant={importMethod === 'file' ? 'contained' : 'outlined'}
                onClick={() => setImportMethod('file')}
                startIcon={<FileIcon />}
                sx={{ flex: 1 }}
              >
                Import ICS File
              </Button>
              <Button
                variant={importMethod === 'google' ? 'contained' : 'outlined'}
                onClick={() => setImportMethod('google')}
                startIcon={<GoogleIcon />}
                sx={{ flex: 1 }}
              >
                Google Calendar
              </Button>
            </Box>

            {importMethod === 'file' && (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Upload an ICS file (.ics or .ical) that you received via email or exported from another calendar.
                </Typography>
              </Box>
            )}

            {importMethod === 'google' && (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Connect to your Google Calendar to import events from your calendar.
                </Typography>
              </Box>
            )}
          </Box>
        );

      case 1:
        if (importMethod === 'file') {
          return (
            <Box>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Select an ICS file to import:
              </Typography>
              
              <Box sx={{ mb: 3 }}>
                <input
                  accept=".ics,.ical"
                  style={{ display: 'none' }}
                  id="ics-file-input"
                  type="file"
                  onChange={handleFileSelect}
                />
                <label htmlFor="ics-file-input">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<UploadIcon />}
                    sx={{ mb: 2 }}
                  >
                    Choose ICS File
                  </Button>
                </label>
                
                {selectedFile && (
                  <Box sx={{ mt: 1 }}>
                    <Chip
                      icon={<FileIcon />}
                      label={selectedFile.name}
                      color="primary"
                      onDelete={() => setSelectedFile(null)}
                    />
                  </Box>
                )}
              </Box>
            </Box>
          );
        } else {
          return (
            <Box>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Connect to Google Calendar:
              </Typography>
              
              <Button
                variant="contained"
                onClick={handleGoogleCalendarAuth}
                startIcon={<GoogleIcon />}
                disabled={loading}
                sx={{ mb: 2 }}
              >
                {loading ? <CircularProgress size={20} /> : 'Connect to Google Calendar'}
              </Button>
              
              {availableCalendars.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <FormControl fullWidth>
                    <InputLabel>Select Calendar</InputLabel>
                    <Select
                      value={selectedCalendar}
                      onChange={(e) => setSelectedCalendar(e.target.value)}
                    >
                      {availableCalendars.map((calendar) => (
                        <MenuItem key={calendar.id} value={calendar.id}>
                          {calendar.summary}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              )}
            </Box>
          );
        }

      case 2:
        return (
          <Box>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Review and select events to import:
            </Typography>

            {validationResult && (
              <Alert 
                severity={validationResult.warnings.length > 0 ? 'warning' : 'success'} 
                sx={{ mb: 2 }}
              >
                Found {validationResult.totalEvents} events. {validationResult.validCount} are within your trip dates.
                {validationResult.warnings.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2">
                      Warnings:
                    </Typography>
                    <ul>
                      {validationResult.warnings.map((warning, index) => (
                        <li key={index}>{warning}</li>
                      ))}
                    </ul>
                  </Box>
                )}
              </Alert>
            )}

            <FormControlLabel
              control={
                <Switch
                  checked={autoDetect}
                  onChange={(e) => setAutoDetect(e.target.checked)}
                />
              }
              label="Auto-select events within trip dates"
              sx={{ mb: 2 }}
            />

            <List sx={{ maxHeight: 300, overflow: 'auto' }}>
              {parsedEvents.map((event, index) => {
                const isSelected = selectedEvents.includes(event);
                const isValid = validationResult?.validEvents.includes(event);
                
                return (
                  <ListItem key={index} divider>
                    <ListItemText
                      primary={event.name || 'Untitled Event'}
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary" component="span">
                            {event.start_datetime ? new Date(event.start_datetime).toLocaleString() : 'No start time'}
                            {event.end_datetime && ` - ${new Date(event.end_datetime).toLocaleString()}`}
                          </Typography>
                          {event.location && (
                            <Typography variant="body2" color="text.secondary" component="span" sx={{ display: 'block' }}>
                              📍 {event.location}
                            </Typography>
                          )}
                          {event.description && (
                            <Typography variant="body2" color="text.secondary" component="span" sx={{ display: 'block' }}>
                              {event.description}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {!isValid && (
                          <WarningIcon color="warning" fontSize="small" />
                        )}
                        <Switch
                          checked={isSelected}
                          onChange={(e) => handleEventToggle(event, e.target.checked)}
                          disabled={!isValid}
                        />
                      </Box>
                    </ListItemSecondaryAction>
                  </ListItem>
                );
              })}
            </List>

            {parsedEvents.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                No events found in the selected source.
              </Typography>
            )}
          </Box>
        );

      case 3:
        return (
          <Box>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Ready to import {selectedEvents.length} events to your trip:
            </Typography>

            <List sx={{ maxHeight: 200, overflow: 'auto', mb: 2 }}>
              {selectedEvents.map((event, index) => (
                <ListItem key={index}>
                  <ListItemText
                    primary={event.name || 'Untitled Event'}
                    secondary={
                      <Typography variant="body2" color="text.secondary" component="span">
                        {event.start_datetime ? new Date(event.start_datetime).toLocaleString() : 'No start time'}
                        {event.location ? ` • ${event.location}` : ''}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>

            <Alert severity="info" sx={{ mb: 2 }}>
              These events will be added to your trip itinerary as group activities.
            </Alert>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '600px' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CalendarIcon />
          Import Calendar Events
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Stepper activeStep={activeStep} orientation="vertical">
          {steps.map((label, index) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
              <StepContent>
                {renderStepContent(index)}
                
                {error && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {error}
                  </Alert>
                )}
                
                <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                  <Button
                    onClick={handleBack}
                    disabled={activeStep === 0}
                  >
                    Back
                  </Button>
                  <Button
                    variant="contained"
                    onClick={
                      activeStep === 0 ? handleNext :
                      activeStep === 1 ? (importMethod === 'file' ? handleParseEvents : handleParseEvents) :
                      activeStep === 2 ? handleNext :
                      handleImportEvents
                    }
                    disabled={
                      (activeStep === 1 && importMethod === 'file' && !selectedFile) ||
                      (activeStep === 1 && importMethod === 'google' && !selectedCalendar) ||
                      loading
                    }
                    startIcon={loading ? <CircularProgress size={20} /> : null}
                  >
                    {activeStep === steps.length - 1 ? 'Import Events' : 'Next'}
                  </Button>
                </Box>
              </StepContent>
            </Step>
          ))}
        </Stepper>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ICSImportDialog;
